// Unified admin CMS — edit every module of the site from one page.
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Home as HomeIcon, LogOut, Save, Loader2, Plus, Trash2, Search, ChevronRight,
  Building2, Sparkles, Users as UsersIcon, MessagesSquare, Radio, GraduationCap,
  Settings, Contact, Bell, Newspaper, Image as ImageIcon, LayoutDashboard,
  AlertCircle, CheckCircle2, X, Edit3, ExternalLink,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import {
  adminListContent, adminUpsertContent, listUniversities,
  adminUpdateUniversity, adminCreateUniversity, adminDeleteUniversity,
  adminUploadUniversityBuilding, adminDeleteUniversityBuilding,
  adminUploadMedia,
} from '../lib/cmsApi';
import { getToken, clearToken, adminMe } from '../lib/adminApi';
import { invalidateContent } from '../hooks/useContent';

const SECTIONS = [
  { id: 'site_branding', label: 'Branding & Contact', icon: Contact, group: 'core' },
  { id: 'hero', label: 'Home — Hero', icon: HomeIcon, group: 'home' },
  { id: 'stats', label: 'Home — Stats', icon: LayoutDashboard, group: 'home' },
  { id: 'marquee', label: 'Home — Top Marquee', icon: Radio, group: 'home' },
  { id: 'advantages', label: 'Home — Advantages', icon: Sparkles, group: 'home' },
  { id: 'testimonials', label: 'Testimonials', icon: MessagesSquare, group: 'social' },
  { id: 'counsellors', label: 'Counsellors', icon: UsersIcon, group: 'social' },
  { id: 'trustedVoices', label: 'Trusted Voices', icon: UsersIcon, group: 'social' },
  { id: 'alumni', label: 'Alumni', icon: GraduationCap, group: 'social' },
  { id: 'faqs_home', label: 'FAQs', icon: MessagesSquare, group: 'content' },
  { id: 'universities', label: 'Universities (66)', icon: Building2, group: 'content' },
  { id: 'blog_posts', label: 'Blog / News', icon: Newspaper, group: 'content' },
  { id: 'notifications', label: 'Notifications', icon: Bell, group: 'system' },
  { id: 'seo_defaults', label: 'SEO Defaults', icon: Settings, group: 'system' },
];

function Toast({ toast, onClose }) {
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, [toast, onClose]);
  if (!toast) return null;
  const Icon = toast.kind === 'err' ? AlertCircle : CheckCircle2;
  return (
    <div
      data-testid="cms-toast"
      className={`fixed top-5 right-5 z-50 flex items-center gap-3 px-4 py-3 rounded-xl shadow-2xl text-white ${
        toast.kind === 'err' ? 'bg-brand' : 'bg-emerald-600'
      }`}
    >
      <Icon className="w-5 h-5" />
      <span className="text-sm font-semibold">{toast.msg}</span>
      <button onClick={onClose} className="ml-2 opacity-70 hover:opacity-100"><X className="w-4 h-4" /></button>
    </div>
  );
}

/* ---------- reusable inputs ---------- */
const Field = ({ label, hint, children }) => (
  <label className="block">
    <div className="text-xs font-bold text-primary uppercase tracking-wider mb-1.5">{label}</div>
    {children}
    {hint && <div className="text-[11px] text-muted-foreground mt-1">{hint}</div>}
  </label>
);

const TextInput = ({ value, onChange, ...rest }) => (
  <input
    value={value ?? ''}
    onChange={(e) => onChange(e.target.value)}
    className="w-full h-10 px-3 rounded-lg border border-border bg-white focus:border-brand focus:ring-2 focus:ring-brand/20 outline-none text-sm"
    {...rest}
  />
);

const TextArea = ({ value, onChange, rows = 3, ...rest }) => (
  <textarea
    value={value ?? ''}
    onChange={(e) => onChange(e.target.value)}
    rows={rows}
    className="w-full px-3 py-2 rounded-lg border border-border bg-white focus:border-brand focus:ring-2 focus:ring-brand/20 outline-none text-sm"
    {...rest}
  />
);

const CheckboxRow = ({ value, onChange, label }) => (
  <label className="inline-flex items-center gap-2 cursor-pointer select-none">
    <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="w-4 h-4 accent-brand" />
    <span className="text-sm text-primary">{label}</span>
  </label>
);

/* Small inline image upload widget used by ListEditor + custom sections */
function ImageField({ value, onChange, folder = 'misc', label = 'Photo', hint }) {
  const API = process.env.REACT_APP_BACKEND_URL;
  const inputRef = React.useRef(null);
  const [uploading, setUploading] = useState(false);
  const preview = value && value.startsWith('/') ? `${API}${value}` : value;
  const onFile = async (file) => {
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) return alert('File too large (max 6 MB)');
    setUploading(true);
    try {
      const res = await adminUploadMedia(file, folder);
      onChange(res.url);
    } catch (e) { alert(e.message); }
    setUploading(false);
  };
  return (
    <div>
      <div className="text-xs font-bold text-primary uppercase tracking-wider mb-1.5">{label}</div>
      <div className="flex items-start gap-3">
        <div className="w-20 h-20 rounded-lg overflow-hidden bg-muted border border-border shrink-0">
          {preview ? <img src={preview} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-[10px] text-muted-foreground">No photo</div>}
        </div>
        <div className="flex-1 space-y-2">
          <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} />
          <div className="flex gap-2 flex-wrap">
            <button type="button" disabled={uploading} onClick={() => inputRef.current?.click()}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-brand hover:bg-brand/90 text-white text-xs font-bold disabled:opacity-60">
              {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImageIcon className="w-3.5 h-3.5" />}
              {uploading ? 'Uploading…' : 'Upload'}
            </button>
            {value && (
              <button type="button" onClick={() => onChange('')} className="h-9 px-3 rounded-lg border border-border hover:bg-muted text-xs font-semibold text-primary">
                Remove
              </button>
            )}
          </div>
          <TextInput value={value} onChange={onChange} placeholder="or paste image URL" />
          {hint && <div className="text-[10px] text-muted-foreground">{hint}</div>}
        </div>
      </div>
    </div>
  );
}

/* ---------- generic list editor ---------- */
function ListEditor({ items, onChange, fields, addLabel = 'Add item', title }) {
  const add = () => {
    const blank = Object.fromEntries(fields.map((f) => [f.key, f.default ?? '']));
    onChange([...(items || []), blank]);
  };
  const update = (i, key, v) => {
    const next = [...items];
    next[i] = { ...next[i], [key]: v };
    onChange(next);
  };
  const displayValue = (raw, f) => {
    if (f.array && Array.isArray(raw)) return raw.join(', ');
    return raw ?? '';
  };
  const parseValue = (v, f) => {
    if (f.array) return v.split(',').map((s) => s.trim()).filter(Boolean);
    return v;
  };
  const remove = (i) => {
    const next = items.filter((_, idx) => idx !== i);
    onChange(next);
  };
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  return (
    <div className="space-y-3">
      {title && <div className="text-sm font-bold text-primary">{title}</div>}
      {(items || []).map((it, i) => (
        <div key={i} className="border border-border rounded-xl p-4 bg-muted/30 relative" data-testid={`list-item-${i}`}>
          <div className="grid gap-3 md:grid-cols-2">
            {fields.map((f) => (
              <div key={f.key} className={f.wide ? 'md:col-span-2' : ''}>
                {f.type === 'image' ? (
                  <ImageField
                    value={it[f.key]} onChange={(v) => update(i, f.key, v)}
                    folder={f.folder || 'misc'} label={f.label} hint={f.hint}
                  />
                ) : (
                  <Field label={f.label} hint={f.hint}>
                    {f.type === 'textarea'
                      ? <TextArea value={displayValue(it[f.key], f)} onChange={(v) => update(i, f.key, parseValue(v, f))} rows={f.rows || 3} />
                      : <TextInput value={displayValue(it[f.key], f)} onChange={(v) => update(i, f.key, parseValue(v, f))} placeholder={f.placeholder} />}
                  </Field>
                )}
              </div>
            ))}
          </div>
          <div className="mt-3 flex items-center gap-2">
            <button
              type="button" onClick={() => move(i, -1)}
              className="text-xs text-primary hover:text-brand disabled:opacity-40"
              disabled={i === 0}
            >↑ Move up</button>
            <button
              type="button" onClick={() => move(i, 1)}
              className="text-xs text-primary hover:text-brand disabled:opacity-40"
              disabled={i === items.length - 1}
            >↓ Move down</button>
            <button
              type="button" onClick={() => remove(i)}
              className="ml-auto inline-flex items-center gap-1 text-xs text-brand hover:text-brand/70"
              data-testid={`list-remove-${i}`}
            >
              <Trash2 className="w-3.5 h-3.5" /> Remove
            </button>
          </div>
        </div>
      ))}
      <button
        type="button" onClick={add}
        data-testid="list-add"
        className="w-full h-11 rounded-xl border-2 border-dashed border-border hover:border-brand hover:bg-brand/5 text-primary text-sm font-semibold inline-flex items-center justify-center gap-2"
      >
        <Plus className="w-4 h-4" /> {addLabel}
      </button>
    </div>
  );
}

/* ---------- section forms ---------- */
function BrandingForm({ value, onChange }) {
  const v = value || {};
  const set = (k) => (val) => onChange({ ...v, [k]: val });
  return (
    <div className="grid gap-4 md:grid-cols-2">
      <Field label="Brand name"><TextInput value={v.name} onChange={set('name')} placeholder="Russian Education" /></Field>
      <Field label="Tagline"><TextInput value={v.tagline} onChange={set('tagline')} /></Field>
      <Field label="Phone"><TextInput value={v.phone} onChange={set('phone')} placeholder="+91 …" /></Field>
      <Field label="Email"><TextInput value={v.email} onChange={set('email')} /></Field>
      <Field label="WhatsApp (E.164)"><TextInput value={v.whatsapp} onChange={set('whatsapp')} placeholder="+919509406802" /></Field>
      <Field label="Office hours"><TextInput value={v.hours} onChange={set('hours')} /></Field>
      <div className="md:col-span-2"><Field label="Address"><TextArea value={v.address} onChange={set('address')} rows={2} /></Field></div>
    </div>
  );
}

function HeroForm({ value, onChange }) {
  const v = value || {};
  const set = (k) => (val) => onChange({ ...v, [k]: val });
  return (
    <div className="grid gap-4">
      <Field label="Hero title"><TextInput value={v.title} onChange={set('title')} /></Field>
      <Field label="Hero subtitle"><TextArea value={v.subtitle} onChange={set('subtitle')} rows={2} /></Field>
      <div className="grid gap-4 md:grid-cols-3">
        <Field label="Primary CTA text"><TextInput value={v.ctaPrimary} onChange={set('ctaPrimary')} /></Field>
        <Field label="Secondary CTA text"><TextInput value={v.ctaSecondary} onChange={set('ctaSecondary')} /></Field>
        <Field label="Trust badge"><TextInput value={v.trustBadge} onChange={set('trustBadge')} /></Field>
      </div>
    </div>
  );
}

function NotificationsForm({ value, onChange }) {
  const v = value || {};
  const set = (k) => (val) => onChange({ ...v, [k]: val });
  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-border bg-muted/40 p-4 text-sm text-primary">
        <div className="font-bold mb-1">How this works</div>
        Server-side credentials for Twilio WhatsApp & Resend Email live in <code className="text-brand">/app/backend/.env</code>. Toggle these switches to let the site fire notifications on every new inquiry. If secrets are missing, the channel silently no-ops.
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <div className="border border-border rounded-xl p-5 bg-white">
          <div className="flex items-center justify-between mb-3">
            <div className="font-bold text-primary">WhatsApp (Twilio)</div>
            <CheckboxRow value={v.whatsappEnabled} onChange={set('whatsappEnabled')} label="Enabled" />
          </div>
          <Field label="Admin WhatsApp"><TextInput value={v.notifyWhatsappTo} onChange={set('notifyWhatsappTo')} placeholder="whatsapp:+91…" /></Field>
        </div>
        <div className="border border-border rounded-xl p-5 bg-white">
          <div className="flex items-center justify-between mb-3">
            <div className="font-bold text-primary">Email (Resend)</div>
            <CheckboxRow value={v.emailEnabled} onChange={set('emailEnabled')} label="Enabled" />
          </div>
          <Field label="Admin email"><TextInput value={v.notifyEmailTo} onChange={set('notifyEmailTo')} placeholder="admin@…" /></Field>
        </div>
      </div>
    </div>
  );
}

function SEOForm({ value, onChange }) {
  const v = value || {};
  const set = (k) => (val) => onChange({ ...v, [k]: val });
  return (
    <div className="grid gap-4">
      <Field label="Title suffix"><TextInput value={v.titleSuffix} onChange={set('titleSuffix')} placeholder=" — Russian Education" /></Field>
      <Field label="Default meta description"><TextArea value={v.defaultDescription} onChange={set('defaultDescription')} rows={3} /></Field>
      <Field label="Default OG image URL"><TextInput value={v.ogImage} onChange={set('ogImage')} placeholder="https://…" /></Field>
    </div>
  );
}

/* ---------- Building image upload widget ---------- */
function BuildingImageUploader({ slug, currentUrl, onChange, toast, disabled }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = React.useRef(null);
  const API = process.env.REACT_APP_BACKEND_URL;

  const preview = currentUrl && currentUrl.startsWith('/') ? `${API}${currentUrl}` : currentUrl;

  const onFile = async (file) => {
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) { toast('err', 'File too large (max 6 MB)'); return; }
    if (!['image/png', 'image/jpeg', 'image/jpg', 'image/webp'].includes(file.type)) {
      toast('err', 'Use PNG, JPG or WebP');
      return;
    }
    setUploading(true);
    try {
      const res = await adminUploadUniversityBuilding(slug, file);
      onChange(res.url);
      toast('ok', 'Image uploaded');
    } catch (e) { toast('err', e.message); }
    setUploading(false);
  };

  const reset = async () => {
    if (!window.confirm('Reset to default stock image?')) return;
    try {
      const res = await adminDeleteUniversityBuilding(slug);
      onChange(res.reset_to);
      toast('ok', 'Reset to default');
    } catch (e) { toast('err', e.message); }
  };

  return (
    <div className="border border-border rounded-xl p-4 bg-muted/20">
      <div className="text-xs font-bold text-primary uppercase tracking-wider mb-3">
        Building / campus photo
      </div>
      <div className="grid gap-4 md:grid-cols-[220px_1fr] items-start">
        <div className="w-full aspect-[4/3] rounded-lg overflow-hidden bg-muted border border-border">
          {preview ? (
            <img src={preview} alt="Building" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.opacity = '0.3'; }} />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-muted-foreground text-xs">No image</div>
          )}
        </div>
        <div className="space-y-3">
          <input
            ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp"
            className="hidden"
            onChange={(e) => onFile(e.target.files?.[0])}
            data-testid="uni-building-file-input"
          />
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={disabled || uploading}
              onClick={() => inputRef.current?.click()}
              data-testid="uni-building-upload-btn"
              className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-brand hover:bg-brand/90 text-white text-sm font-bold disabled:opacity-60"
            >
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />}
              {uploading ? 'Uploading…' : 'Upload photo'}
            </button>
            <button
              type="button" onClick={reset} disabled={disabled}
              className="h-10 px-4 rounded-lg border border-border hover:bg-muted text-sm font-semibold text-primary"
            >
              Reset to default
            </button>
          </div>
          <div className="text-[11px] text-muted-foreground">
            {disabled
              ? 'Save the new university first, then upload a photo.'
              : 'PNG, JPG or WebP · Max 6 MB · Landscape 4:3 or wider works best.'}
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">or paste an image URL</div>
            <TextInput value={currentUrl} onChange={onChange} placeholder="https://…" />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- Universities editor ---------- */
function UniversitiesEditor({ toast }) {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState('');
  const [editing, setEditing] = useState(null); // slug or 'new'
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { setItems(await listUniversities()); } catch (e) { toast('err', e.message); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return items;
    return items.filter((u) => (u.name || '').toLowerCase().includes(s) || (u.city || '').toLowerCase().includes(s) || (u.short || '').toLowerCase().includes(s));
  }, [items, q]);

  const startEdit = (u) => { setEditing(u.slug); setDraft({ ...u }); };
  const startNew = () => { setEditing('new'); setDraft({ slug: '', name: '', short: '', city: '', region: '', founded: 2000, annualFeeUSD: 4000, hostelUSD: 400, ranking: '', type: 'State Medical University', total: '', indian: '' }); };
  const cancel = () => { setEditing(null); setDraft(null); };

  const save = async () => {
    setSaving(true);
    try {
      if (editing === 'new') {
        const created = await adminCreateUniversity(draft);
        toast('ok', `Created ${created.name}`);
      } else {
        await adminUpdateUniversity(editing, draft);
        toast('ok', `Updated ${draft.name}`);
      }
      cancel();
      await load();
    } catch (e) { toast('err', e.message); }
    setSaving(false);
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete "${u.name}" permanently?`)) return;
    try { await adminDeleteUniversity(u.slug); toast('ok', 'Deleted'); await load(); }
    catch (e) { toast('err', e.message); }
  };

  if (editing) {
    const setF = (k) => (v) => setDraft({ ...draft, [k]: v });
    return (
      <div>
        <button onClick={cancel} className="text-xs text-brand font-semibold mb-4">← Back to list</button>
        <div className="grid gap-4 md:grid-cols-2">
          <Field label="Slug (kebab-case, unique)"><TextInput value={draft.slug} onChange={setF('slug')} disabled={editing !== 'new'} placeholder="my-medical-university" /></Field>
          <Field label="Short name"><TextInput value={draft.short} onChange={setF('short')} placeholder="KFU" /></Field>
          <div className="md:col-span-2"><Field label="Full name"><TextInput value={draft.name} onChange={setF('name')} /></Field></div>
          <Field label="City"><TextInput value={draft.city} onChange={setF('city')} /></Field>
          <Field label="Region / State"><TextInput value={draft.region} onChange={setF('region')} /></Field>
          <Field label="Founded (year)"><TextInput value={draft.founded} onChange={(v) => setF('founded')(Number(v) || 0)} type="number" /></Field>
          <Field label="Type"><TextInput value={draft.type} onChange={setF('type')} /></Field>
          <Field label="Ranking"><TextInput value={draft.ranking} onChange={setF('ranking')} placeholder="Top 10" /></Field>
          <Field label="Total students"><TextInput value={draft.total || draft.students} onChange={setF('total')} placeholder="5,000+" /></Field>
          <Field label="Annual fee (USD)"><TextInput value={draft.annualFeeUSD} onChange={(v) => setF('annualFeeUSD')(Number(v) || 0)} type="number" /></Field>
          <Field label="Hostel fee/year (USD)"><TextInput value={draft.hostelUSD} onChange={(v) => setF('hostelUSD')(Number(v) || 0)} type="number" /></Field>
          <Field label="Indian students"><TextInput value={draft.indian || draft.indianStudents} onChange={setF('indian')} placeholder="500+" /></Field>
          <Field label="Fee display string"><TextInput value={draft.fee} onChange={setF('fee')} placeholder="₽405,000" /></Field>
          <div className="md:col-span-2">
            <BuildingImageUploader
              slug={draft.slug}
              currentUrl={draft.building}
              onChange={setF('building')}
              toast={toast}
              disabled={editing === 'new'}
            />
          </div>
          <div className="md:col-span-2"><Field label="About (long description)"><TextArea value={draft.about} onChange={setF('about')} rows={6} /></Field></div>
          <div className="md:col-span-2"><Field label="Hostel info"><TextArea value={draft.hostelInfo} onChange={setF('hostelInfo')} rows={2} /></Field></div>
          <div className="md:col-span-2"><Field label="Climate info"><TextArea value={draft.climateInfo} onChange={setF('climateInfo')} rows={2} /></Field></div>
          <div className="md:col-span-2 flex items-center gap-4">
            <CheckboxRow value={draft.nmc} onChange={setF('nmc')} label="NMC approved" />
            <CheckboxRow value={draft.who} onChange={setF('who')} label="WHO listed" />
            <CheckboxRow value={draft.faimer} onChange={setF('faimer')} label="FAIMER" />
            <CheckboxRow value={draft.mcc} onChange={setF('mcc')} label="MCC (Canada)" />
          </div>
        </div>
        <div className="mt-6 flex gap-3">
          <button
            onClick={save} disabled={saving}
            data-testid="uni-save-btn"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-brand hover:bg-brand/90 text-white font-bold text-sm shadow-lg shadow-brand/30 disabled:opacity-60"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {editing === 'new' ? 'Create' : 'Save changes'}
          </button>
          <button onClick={cancel} className="h-11 px-5 rounded-xl border border-border text-sm font-semibold hover:bg-muted">Cancel</button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-3 mb-5">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, city, short…"
            data-testid="uni-search"
            className="w-full h-10 pl-9 pr-3 rounded-lg border border-border bg-white focus:border-brand outline-none text-sm"
          />
        </div>
        <button
          onClick={startNew}
          data-testid="uni-new-btn"
          className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-brand hover:bg-brand/90 text-white text-sm font-bold"
        >
          <Plus className="w-4 h-4" /> Add university
        </button>
      </div>
      {loading ? (
        <div className="p-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-brand" /></div>
      ) : (
        <div className="grid gap-2">
          {filtered.map((u) => {
            const API = process.env.REACT_APP_BACKEND_URL;
            const img = u.building && u.building.startsWith('/') ? `${API}${u.building}` : u.building;
            return (
            <div key={u.slug} className="flex items-center gap-3 p-3 rounded-lg border border-border hover:border-brand hover:shadow-md bg-white transition">
              <div className="w-16 h-12 rounded-md overflow-hidden bg-muted border border-border shrink-0">
                {img ? <img src={img} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-[10px] text-muted-foreground">No img</div>}
              </div>
              <div className="w-10 h-10 rounded-lg bg-primary text-white text-xs font-bold flex items-center justify-center shrink-0">{u.logo || (u.short || '?').slice(0, 2).toUpperCase()}</div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-primary text-sm truncate">{u.name}</div>
                <div className="text-xs text-muted-foreground truncate">{u.city} · {u.region} · {u.ranking}</div>
              </div>
              <Link
                to={`/universities/${u.slug}`} target="_blank"
                className="text-xs text-primary hover:text-brand inline-flex items-center gap-1"
                title="View on site"
              ><ExternalLink className="w-3.5 h-3.5" /></Link>
              <button
                onClick={() => startEdit(u)}
                data-testid={`uni-edit-${u.slug}`}
                className="inline-flex items-center gap-1 h-8 px-3 rounded-md bg-primary/5 hover:bg-brand hover:text-white text-primary text-xs font-semibold"
              >
                <Edit3 className="w-3.5 h-3.5" /> Edit
              </button>
              <button
                onClick={() => remove(u)}
                className="inline-flex items-center gap-1 h-8 px-2 rounded-md hover:bg-brand/10 text-brand text-xs font-semibold"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          );})}
          {filtered.length === 0 && <div className="text-sm text-muted-foreground p-8 text-center">No universities match &quot;{q}&quot;</div>}
        </div>
      )}
    </div>
  );
}

/* ---------- main component ---------- */
export default function AdminCMS() {
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [items, setItems] = useState({});      // {key: value}
  const [dirty, setDirty] = useState({});      // {key: bool}
  const [active, setActive] = useState('site_branding');
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [loading, setLoading] = useState(true);

  const showToast = (kind, msg) => setToast({ kind, msg });

  useEffect(() => {
    if (!getToken()) { nav('/admin/login', { replace: true }); return; }
    (async () => {
      try {
        const [meRes, list] = await Promise.all([adminMe(), adminListContent()]);
        setMe(meRes);
        const map = {};
        list.forEach((d) => { map[d.key] = d.value; });
        setItems(map);
      } catch (e) {
        if (e.status === 401) nav('/admin/login', { replace: true });
        else showToast('err', e.message);
      }
      setLoading(false);
    })();
  }, [nav]);

  const setValue = (key, value) => {
    setItems((s) => ({ ...s, [key]: value }));
    setDirty((s) => ({ ...s, [key]: true }));
  };

  const saveKey = async (key) => {
    setSaving(true);
    try {
      await adminUpsertContent(key, items[key]);
      invalidateContent();
      setDirty((s) => ({ ...s, [key]: false }));
      showToast('ok', 'Saved successfully');
    } catch (e) { showToast('err', e.message); }
    setSaving(false);
  };

  const logout = () => { clearToken(); nav('/admin/login', { replace: true }); };

  const section = SECTIONS.find((s) => s.id === active);

  const renderSection = () => {
    if (active === 'universities') return <UniversitiesEditor toast={showToast} />;

    const val = items[active];
    const set = (v) => setValue(active, v);

    if (active === 'site_branding') return <BrandingForm value={val} onChange={set} />;
    if (active === 'hero') return <HeroForm value={val} onChange={set} />;
    if (active === 'notifications') return <NotificationsForm value={val} onChange={set} />;
    if (active === 'seo_defaults') return <SEOForm value={val} onChange={set} />;
    if (active === 'stats') return (
      <ListEditor
        items={val || []} onChange={set}
        addLabel="Add stat"
        fields={[
          { key: 'value', label: 'Value', placeholder: '1500+' },
          { key: 'label', label: 'Label', placeholder: 'Students Placed' },
        ]}
      />
    );
    if (active === 'marquee') return (
      <ListEditor
        items={val || []} onChange={set}
        addLabel="Add marquee item"
        fields={[
          { key: 'icon', label: 'Lucide icon name', placeholder: 'FileEdit', hint: 'Any lucide-react icon (case-sensitive)' },
          { key: 'text', label: 'Text', wide: true },
        ]}
      />
    );
    if (active === 'advantages') return (
      <ListEditor
        items={val || []} onChange={set}
        addLabel="Add advantage"
        fields={[
          { key: 'title', label: 'Title' },
          { key: 'subtitle', label: 'Subtitle' },
          { key: 'highlight', label: 'Highlight value', placeholder: '195+' },
          { key: 'highlightLabel', label: 'Highlight caption' },
          { key: 'icon', label: 'Lucide icon' },
          { key: 'points', label: 'Bullets (comma-separated)', wide: true, placeholder: 'Point A, Point B, Point C', array: true,
            hint: 'Comma-separated on screen, stored as array.' },
        ]}
      />
    );
    if (active === 'testimonials') return (
      <ListEditor
        items={val || []} onChange={set}
        addLabel="Add testimonial"
        fields={[
          { key: 'photo', label: 'Photo (optional)', type: 'image', folder: 'testimonials', wide: true },
          { key: 'initials', label: 'Initials', placeholder: 'PS' },
          { key: 'name', label: 'Full name' },
          { key: 'year', label: 'Year of study' },
          { key: 'uni', label: 'University' },
          { key: 'state', label: 'State' },
          { key: 'quote', label: 'Quote', type: 'textarea', wide: true, rows: 4 },
        ]}
      />
    );
    if (active === 'counsellors') return (
      <ListEditor
        items={val || []} onChange={set}
        addLabel="Add counsellor"
        fields={[
          { key: 'photo', label: 'Counsellor photo', type: 'image', folder: 'counsellors', wide: true },
          { key: 'initials', label: 'Initials' },
          { key: 'name', label: 'Full name' },
          { key: 'years', label: 'Years of experience' },
          { key: 'students', label: 'Students placed' },
          { key: 'reviews', label: 'Reviews count' },
          { key: 'city', label: 'City' },
          { key: 'state', label: 'State' },
          { key: 'rating', label: 'Rating (0-5)' },
          { key: 'tags', label: 'Tags (comma-separated)', wide: true, array: true },
        ]}
      />
    );
    if (active === 'trustedVoices') return (
      <ListEditor
        items={val || []} onChange={set}
        addLabel="Add voice"
        fields={[
          { key: 'poster', label: 'Poster / thumbnail image', type: 'image', folder: 'voices', wide: true, hint: 'Vertical 9:16 poster looks best (Reels-style).' },
          { key: 'youtubeId', label: 'YouTube video ID (optional)', hint: 'e.g. dQw4w9WgXcQ — clicking the poster will open this video.' },
          { key: 'views', label: 'Views' },
          { key: 'name', label: 'Name' },
          { key: 'year', label: 'Year' },
          { key: 'city', label: 'City' },
          { key: 'state', label: 'State' },
          { key: 'quote', label: 'Quote', wide: true },
        ]}
      />
    );
    if (active === 'alumni') return (
      <ListEditor
        items={val || []} onChange={set}
        addLabel="Add alumnus"
        fields={[
          { key: 'photo', label: 'Alumnus photo', type: 'image', folder: 'alumni', wide: true },
          { key: 'initials', label: 'Initials (fallback if no photo)' },
          { key: 'name', label: 'Full name (Dr. …)' },
          { key: 'uni', label: 'University' },
          { key: 'year', label: 'Graduation year' },
        ]}
      />
    );
    if (active === 'faqs_home') return (
      <ListEditor
        items={val || []} onChange={set}
        addLabel="Add FAQ"
        fields={[
          { key: 'q', label: 'Question', wide: true },
          { key: 'a', label: 'Answer', type: 'textarea', wide: true, rows: 4 },
        ]}
      />
    );
    if (active === 'blog_posts') return (
      <ListEditor
        items={val || []} onChange={set}
        addLabel="Add blog post"
        fields={[
          { key: 'cover', label: 'Cover image', type: 'image', folder: 'blog', wide: true },
          { key: 'slug', label: 'Slug', placeholder: 'my-first-post' },
          { key: 'title', label: 'Title', wide: true },
          { key: 'excerpt', label: 'Excerpt', type: 'textarea', wide: true, rows: 2 },
          { key: 'body', label: 'Body (Markdown ok)', type: 'textarea', wide: true, rows: 8 },
          { key: 'published_at', label: 'Published at (ISO date)' },
        ]}
      />
    );
    return <div className="text-sm text-muted-foreground">Section coming soon</div>;
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-muted/30">
      <Loader2 className="w-8 h-8 animate-spin text-brand" />
    </div>
  );

  return (
    <div className="min-h-screen bg-muted/30" data-testid="admin-cms-page">
      <SEO title="Content Manager — Admin" description="Russian Education CMS" noindex />
      <Toast toast={toast} onClose={() => setToast(null)} />

      <div className="bg-primary text-white sticky top-0 z-40 shadow-lg">
        <div className="max-w-[1600px] mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-brand flex items-center justify-center">
              <Settings className="w-5 h-5" />
            </div>
            <div>
              <div className="font-serif font-bold text-lg">Content Manager</div>
              <div className="text-[11px] opacity-70">Russian Education · Full-site CMS</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/admin" className="text-xs opacity-80 hover:opacity-100 inline-flex items-center gap-1 px-3 py-1.5 rounded-md hover:bg-white/10">
              <LayoutDashboard className="w-4 h-4" /> Dashboard
            </Link>
            <Link to="/admin/universities" className="text-xs opacity-80 hover:opacity-100 inline-flex items-center gap-1 px-3 py-1.5 rounded-md hover:bg-white/10">
              <ImageIcon className="w-4 h-4" /> Logos
            </Link>
            <Link to="/admin/gallery" className="text-xs opacity-80 hover:opacity-100 inline-flex items-center gap-1 px-3 py-1.5 rounded-md hover:bg-white/10">
              <ImageIcon className="w-4 h-4" /> Gallery
            </Link>
            <div className="w-px h-6 bg-white/20 mx-1" />
            <div className="text-xs opacity-80">{me?.email}</div>
            <button
              onClick={logout} data-testid="cms-logout"
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md hover:bg-white/10 text-xs"
            >
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto px-6 py-6 grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
        {/* Sidebar */}
        <aside className="bg-white rounded-2xl border border-border p-3 h-fit lg:sticky lg:top-24">
          {['core', 'home', 'social', 'content', 'system'].map((g) => (
            <div key={g} className="mb-2">
              <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground px-3 py-2">
                {g === 'core' ? 'Core' : g === 'home' ? 'Home page' : g === 'social' ? 'Social proof' : g === 'content' ? 'Content' : 'System'}
              </div>
              {SECTIONS.filter((s) => s.group === g).map((s) => {
                const Icon = s.icon;
                const isActive = active === s.id;
                const isDirty = dirty[s.id];
                return (
                  <button
                    key={s.id} onClick={() => setActive(s.id)}
                    data-testid={`cms-tab-${s.id}`}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold transition ${
                      isActive ? 'bg-brand text-white shadow-md shadow-brand/30' : 'text-primary hover:bg-muted'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="flex-1 text-left">{s.label}</span>
                    {isDirty && <span className="w-2 h-2 rounded-full bg-amber-400" title="Unsaved" />}
                    {isActive && <ChevronRight className="w-4 h-4" />}
                  </button>
                );
              })}
            </div>
          ))}
        </aside>

        {/* Main content */}
        <main className="bg-white rounded-2xl border border-border p-6 md:p-8">
          <div className="flex items-start justify-between mb-6 gap-4">
            <div>
              <h1 className="font-serif text-2xl font-bold text-primary">{section?.label}</h1>
              <p className="text-sm text-muted-foreground mt-1">
                {active === 'universities'
                  ? 'Add, edit or delete any of the 64 universities. Changes reflect immediately on the public site.'
                  : 'Edit the section below and click Save. Changes are live within a few seconds.'}
              </p>
            </div>
            {active !== 'universities' && (
              <button
                onClick={() => saveKey(active)}
                disabled={saving || !dirty[active]}
                data-testid="cms-save-btn"
                className={`inline-flex items-center gap-2 h-11 px-5 rounded-xl text-white font-bold text-sm shadow-lg transition ${
                  dirty[active] ? 'bg-brand hover:bg-brand/90 shadow-brand/30' : 'bg-muted-foreground/30 cursor-not-allowed shadow-none'
                }`}
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {dirty[active] ? 'Save changes' : 'Saved'}
              </button>
            )}
          </div>

          <div>{renderSection()}</div>
        </main>
      </div>
    </div>
  );
}
